import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate, getMonthName, getCurrentYear, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, ChefHat, Truck, ShoppingBag, Package, Eye, Clock, CheckCircle, XCircle, ChevronDown, ChevronRight, Search, FileText } from 'lucide-react';
import { format, parseISO, isToday, isPast, addDays, startOfWeek, endOfWeek, eachDayOfInterval } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';
import { SnapshotButtons } from '../components/SnapshotButtons';

const STATUTS = {
  en_attente: { label: 'En attente', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', icon: Clock },
  confirmee: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: CheckCircle },
  livree: { label: 'Livrée', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: Package },
  annulee: { label: 'Annulée', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: XCircle },
};

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.en_attente;
  const Icon = s.icon;
  return <Badge className={`gap-1 ${s.color}`}><Icon className="w-3 h-3" />{s.label}</Badge>;
}

function ModeBadge({ mode }) {
  if (mode === 'click_collect') {
    return <Badge variant="outline" className="gap-1 border-purple-300 text-purple-700 dark:text-purple-400"><ShoppingBag className="w-3 h-3" />Click & Collect</Badge>;
  }
  return <Badge variant="outline" className="gap-1 border-blue-300 text-blue-700 dark:text-blue-400"><Truck className="w-3 h-3" />Livraison</Badge>;
}

function CommandeForm({ item, catalog, onClose, onSave }) {
  const allProducts = [];
  Object.entries(catalog).forEach(([cat, items]) => {
    items.forEach(p => allProducts.push({ ...p, category: cat }));
  });

  const [form, setForm] = useState(item ? {
    nom: item.nom, prenom: item.prenom, adresse: item.adresse || '',
    date_livraison: item.date_livraison, mode: item.mode,
    details: { ...item.details }, statut: item.statut, notes: item.notes || ''
  } : {
    nom: '', prenom: '', adresse: '',
    date_livraison: toISODateString(new Date()), mode: 'livraison',
    details: {}, statut: 'en_attente', notes: ''
  });
  const [dateOpen, setDateOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openCats, setOpenCats] = useState(new Set(
    Object.keys(catalog).filter(cat =>
      catalog[cat].some(p => (form.details[p.key] || 0) > 0)
    )
  ));

  const totalPieces = Object.values(form.details).reduce((s, v) => s + (v || 0), 0);
  const totalPrix = allProducts.reduce((s, p) => s + (form.details[p.key] || 0) * p.prix, 0);

  const updateDetail = (key, val) => {
    const num = Math.max(0, parseInt(val) || 0);
    setForm(prev => {
      const newDetails = { ...prev.details };
      if (num === 0) delete newDetails[key]; else newDetails[key] = num;
      return { ...prev, details: newDetails };
    });
  };

  const toggleCat = (cat) => {
    setOpenCats(prev => {
      const n = new Set(prev);
      n.has(cat) ? n.delete(cat) : n.add(cat);
      return n;
    });
  };

  const filteredCatalog = {};
  Object.entries(catalog).forEach(([cat, items]) => {
    const filtered = searchTerm
      ? items.filter(p => p.label.toLowerCase().includes(searchTerm.toLowerCase()))
      : items;
    if (filtered.length > 0) filteredCatalog[cat] = filtered;
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom) { toast.error('Nom et prénom requis'); return; }
    if (totalPieces === 0) { toast.error('Ajoutez au moins un produit'); return; }
    if (form.mode === 'livraison' && !form.adresse) { toast.error('Adresse requise pour la livraison'); return; }
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Nom *</Label>
          <Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="Nom" data-testid="input-nom" />
        </div>
        <div className="space-y-1">
          <Label>Prénom *</Label>
          <Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} placeholder="Prénom" data-testid="input-prenom" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Date de livraison *</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="date-livraison-btn">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {form.date_livraison ? format(parseISO(form.date_livraison), 'PPP', { locale: fr }) : 'Choisir'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar mode="single" selected={form.date_livraison ? parseISO(form.date_livraison) : undefined}
                onSelect={(date) => { if (date) { setForm({ ...form, date_livraison: toISODateString(date) }); setDateOpen(false); } }} locale={fr} />
            </PopoverContent>
          </Popover>
        </div>
        <div className="space-y-1">
          <Label>Mode *</Label>
          <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
            <SelectTrigger data-testid="select-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="livraison"><span className="flex items-center gap-2"><Truck className="w-4 h-4" /> Livraison</span></SelectItem>
              <SelectItem value="click_collect"><span className="flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Click & Collect</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {form.mode === 'livraison' && (
        <div className="space-y-1">
          <Label>Adresse de livraison *</Label>
          <Input value={form.adresse} onChange={(e) => setForm({ ...form, adresse: e.target.value })} placeholder="Adresse complète" data-testid="input-adresse" />
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-base font-semibold">Produits</Label>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">{totalPieces} pièce(s)</Badge>
            {totalPrix > 0 && <Badge className="text-sm bg-green-600">{totalPrix.toFixed(2)} EUR</Badge>}
          </div>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechercher un produit..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9 h-9" data-testid="search-products" />
        </div>

        <div className="space-y-1 max-h-[35vh] overflow-y-auto border rounded-lg">
          {Object.entries(filteredCatalog).map(([cat, items]) => {
            const isOpen = openCats.has(cat) || searchTerm;
            const catTotal = items.reduce((s, p) => s + (form.details[p.key] || 0), 0);
            return (
              <div key={cat}>
                <button type="button" onClick={() => toggleCat(cat)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold hover:bg-muted/60 transition-colors border-b">
                  <span className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    {cat}
                  </span>
                  {catTotal > 0 && <Badge variant="default" className="text-xs">{catTotal}</Badge>}
                </button>
                {isOpen && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 p-2">
                    {items.map(p => {
                      const qty = form.details[p.key] || 0;
                      return (
                        <div key={p.key} className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                          qty > 0 ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'
                        }`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate leading-tight">{p.label}</p>
                            {p.prix > 0 && <p className="text-[10px] text-muted-foreground">{p.prix.toFixed(2)} EUR</p>}
                          </div>
                          <Input type="number" min="0" value={qty || ''} onChange={e => updateDetail(p.key, e.target.value)}
                            className="w-14 text-center font-mono text-sm h-8 p-0" placeholder="0" data-testid={`qty-${p.key}`} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {item && (
        <div className="space-y-1">
          <Label>Statut</Label>
          <Select value={form.statut} onValueChange={(v) => setForm({ ...form, statut: v })}>
            <SelectTrigger data-testid="select-statut"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(STATUTS).map(([key, s]) => (
                <SelectItem key={key} value={key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes, allergies, instructions..." rows={2} data-testid="input-notes" />
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
        <Button type="submit" data-testid="submit-commande-btn">{item ? 'Modifier' : 'Créer la commande'}</Button>
      </DialogFooter>
    </form>
  );
}

function PlanningView({ commandes, onEdit }) {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const days = eachDayOfInterval({ start: weekStart, end: endOfWeek(weekStart, { weekStartsOn: 1 }) });
  const prev = () => setWeekStart(addDays(weekStart, -7));
  const next = () => setWeekStart(addDays(weekStart, 7));
  const today = () => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={prev}>&larr;</Button>
          <Button variant="outline" size="sm" onClick={today}>Aujourd'hui</Button>
          <Button variant="outline" size="sm" onClick={next}>&rarr;</Button>
        </div>
        <h3 className="font-semibold text-lg">{format(days[0], 'd MMM', { locale: fr })} - {format(days[6], 'd MMM yyyy', { locale: fr })}</h3>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map(day => {
          const dayStr = toISODateString(day);
          const dayCommandes = commandes.filter(c => c.date_livraison === dayStr);
          const isCurrentDay = isToday(day);
          const past = isPast(day) && !isCurrentDay;
          return (
            <div key={dayStr} className={`min-h-[140px] rounded-xl border-2 p-2 transition-colors ${
              isCurrentDay ? 'border-primary bg-primary/5' : past ? 'border-border/50 bg-muted/30' : 'border-border bg-card'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold uppercase ${isCurrentDay ? 'text-primary' : 'text-muted-foreground'}`}>{format(day, 'EEE', { locale: fr })}</span>
                <span className={`text-sm font-bold ${isCurrentDay ? 'text-primary' : ''}`}>{format(day, 'd')}</span>
              </div>
              {dayCommandes.length > 0 && <Badge variant="secondary" className="mb-1 text-[10px]">{dayCommandes.length} cmd</Badge>}
              <div className="space-y-1">
                {dayCommandes.slice(0, 3).map(c => (
                  <button key={c.id} onClick={() => onEdit(c)} className="w-full text-left p-1.5 rounded-lg text-[11px] leading-tight border border-border/50 hover:bg-muted/80 transition-colors truncate">
                    <div className="font-medium truncate">{c.prenom} {c.nom}</div>
                    <div className="text-muted-foreground">{c.total_pieces}pc - {c.mode === 'livraison' ? 'Livr.' : 'C&C'}</div>
                  </button>
                ))}
                {dayCommandes.length > 3 && <p className="text-[10px] text-muted-foreground text-center">+{dayCommandes.length - 3} autres</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailView({ commande, catalog, onClose, onEdit, api }) {
  if (!commande) return null;
  const allProducts = {};
  Object.values(catalog).flat().forEach(p => { allProducts[p.key] = p; });
  const activeProducts = Object.entries(commande.details || {}).filter(([, v]) => v > 0);
  const totalPrix = activeProducts.reduce((s, [key, qty]) => {
    const p = allProducts[key];
    return s + (p ? p.prix * qty : 0);
  }, 0);

  const handleDevis = async () => {
    try {
      const res = await api.get(`/traiteur/${commande.id}/devis`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${commande.prenom}_${commande.nom}_${commande.date_livraison}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Devis téléchargé');
    } catch { toast.error('Erreur lors de la génération du devis'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold">{commande.prenom} {commande.nom}</h3>
          <p className="text-sm text-muted-foreground">{formatDate(commande.date_livraison)}</p>
        </div>
        <div className="flex gap-2">
          <StatutBadge statut={commande.statut} />
          <ModeBadge mode={commande.mode} />
        </div>
      </div>
      {commande.adresse && (
        <div className="p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground mb-1">Adresse de livraison</p>
          <p className="text-sm font-medium">{commande.adresse}</p>
        </div>
      )}
      <div>
        <h4 className="font-semibold mb-3">Détail commande ({commande.total_pieces} pièces)</h4>
        <div className="grid grid-cols-2 gap-2">
          {activeProducts.map(([key, qty]) => {
            const p = allProducts[key];
            return (
              <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-primary/5 border border-primary/20">
                <div className="min-w-0 flex-1">
                  <span className="font-medium text-sm">{p ? p.label : key}</span>
                  {p && p.prix > 0 && <span className="text-xs text-muted-foreground ml-1">({p.prix.toFixed(2)} EUR)</span>}
                </div>
                <div className="text-right ml-2">
                  <span className="text-lg font-bold text-primary tabular-nums">{qty}</span>
                  {p && p.prix > 0 && <p className="text-xs text-muted-foreground">{(qty * p.prix).toFixed(2)} EUR</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {totalPrix > 0 && (
        <div className="flex items-center justify-between p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
          <span className="font-semibold text-green-800 dark:text-green-300">Total</span>
          <span className="text-xl font-bold text-green-700 dark:text-green-400">{totalPrix.toFixed(2)} EUR</span>
        </div>
      )}
      {commande.notes && (
        <div className="p-3 bg-amber-50 dark:bg-amber-950/20 rounded-lg border border-amber-200">
          <p className="text-xs text-amber-700 dark:text-amber-400 mb-1">Notes</p>
          <p className="text-sm">{commande.notes}</p>
        </div>
      )}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Fermer</Button>
        <Button variant="outline" className="gap-2" onClick={handleDevis} data-testid="btn-devis"><FileText className="w-4 h-4" />Devis PDF</Button>
        <Button onClick={() => onEdit(commande)}>Modifier</Button>
      </DialogFooter>
    </div>
  );
}

export function TraiteurPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterStatut, setFilterStatut] = useState('all');
  const [tab, setTab] = useState('planning');

  const fetchData = useCallback(async () => {
    try {
      const [res, catRes] = await Promise.all([api.get('/traiteur'), api.get('/traiteur/catalog')]);
      setData(res.data);
      setCatalog(catRes.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (form) => {
    try {
      if (editItem) {
        await api.put(`/traiteur/${editItem.id}`, form);
      } else {
        await api.post('/traiteur', form);
      }
      toast.success(editItem ? 'Commande modifiée' : 'Commande créée');
      setDialogOpen(false);
      setEditItem(null);
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/traiteur/${id}`);
      toast.success('Supprimé');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleBulkDelete = async () => {
    try {
      await api.post('/traiteur/delete-bulk', { ids: [...selectedIds] });
      toast.success(`${selectedIds.size} commande(s) supprimée(s)`);
      setSelectedIds(new Set());
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleStatutChange = async (id, statut) => {
    try {
      await api.put(`/traiteur/${id}/statut`, { statut });
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const openCreate = () => { setEditItem(null); setDialogOpen(true); };
  const openEdit = (item) => { setEditItem(item); setDialogOpen(true); setDetailOpen(false); };
  const openDetail = (item) => { setViewItem(item); setDetailOpen(true); };
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const filteredData = filterStatut === 'all' ? data : data.filter(c => c.statut === filterStatut);

  const allProducts = {};
  Object.values(catalog).flat().forEach(p => { allProducts[p.key] = p; });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="module-title" data-testid="traiteur-title">Traiteur</h1>
          <p className="text-muted-foreground mt-1">Gestion des commandes traiteur</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <SnapshotButtons module="traiteur" moduleLabel="Traiteur" />
          <ExportButtons api={api} moduleKey="traiteur" />
          <Button onClick={openCreate} className="gap-2" data-testid="btn-new-commande"><Plus className="w-4 h-4" />Nouvelle commande</Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="planning">Planning</TabsTrigger>
          <TabsTrigger value="liste">Liste ({filteredData.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="planning">
          <Card>
            <CardContent className="p-4">
              <PlanningView commandes={data} onEdit={openDetail} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="liste">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Commandes</CardTitle>
                <div className="flex gap-2 items-center">
                  {selectedIds.size > 0 && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" className="gap-1"><Trash2 className="w-3 h-3" />Supprimer ({selectedIds.size})</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Confirmer la suppression</AlertDialogTitle><AlertDialogDescription>Supprimer {selectedIds.size} commande(s) ?</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleBulkDelete}>Supprimer</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Select value={filterStatut} onValueChange={setFilterStatut}>
                    <SelectTrigger className="w-[150px] h-8"><SelectValue placeholder="Filtrer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      {Object.entries(STATUTS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"><Checkbox checked={selectedIds.size === filteredData.length && filteredData.length > 0}
                      onCheckedChange={(c) => setSelectedIds(c ? new Set(filteredData.map(d => d.id)) : new Set())} /></TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Produits</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">Aucune commande traiteur</TableCell></TableRow>
                  ) : filteredData.map(c => {
                    const activeCount = Object.values(c.details || {}).filter(v => v > 0).length;
                    return (
                      <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(c)}>
                        <TableCell onClick={e => e.stopPropagation()}><Checkbox checked={selectedIds.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} /></TableCell>
                        <TableCell className="font-medium">{c.prenom} {c.nom}</TableCell>
                        <TableCell className="text-sm">{formatDate(c.date_livraison)}</TableCell>
                        <TableCell><ModeBadge mode={c.mode} /></TableCell>
                        <TableCell><Badge variant="secondary">{c.total_pieces} pc / {activeCount} ref</Badge></TableCell>
                        <TableCell onClick={e => e.stopPropagation()}>
                          <Select value={c.statut} onValueChange={(v) => handleStatutChange(c.id, v)}>
                            <SelectTrigger className="h-7 w-[130px]"><SelectValue /></SelectTrigger>
                            <SelectContent>{Object.entries(STATUTS).map(([k, s]) => <SelectItem key={k} value={k}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openDetail(c)}><Eye className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button></AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader><AlertDialogTitle>Supprimer ?</AlertDialogTitle><AlertDialogDescription>Supprimer la commande de {c.prenom} {c.nom} ?</AlertDialogDescription></AlertDialogHeader>
                                <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(c.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ChefHat className="w-5 h-5" />{editItem ? 'Modifier la commande' : 'Nouvelle commande traiteur'}</DialogTitle>
          </DialogHeader>
          <CommandeForm item={editItem} catalog={catalog} onClose={() => setDialogOpen(false)} onSave={handleSave} />
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ChefHat className="w-5 h-5" /> Détail commande</DialogTitle>
          </DialogHeader>
          <DetailView commande={viewItem} catalog={catalog} onClose={() => setDetailOpen(false)} onEdit={openEdit} api={api} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
